import { logger } from '@/ui/utilities/instrumentation'
import { isAxiosError } from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'

// 4xx responses are typically user-recoverable (validation failed, auth expired, etc.)
// — log as a warning so they don't surface in Sentry as errors. 5xx, network failures,
// or unexpected exceptions are not self-recoverable and deserve a real error report.
function reportSaveFailure(
	error: unknown,
	options: { resourceName?: string; action: 'save' | 'flush'; lifecycleState: LifecycleState },
) {
	const status = isAxiosError(error) ? error.response?.status : undefined
	const data = {
		resourceName: options.resourceName,
		lifecycleState: options.lifecycleState,
		persistenceState: 'error' as const,
		httpStatus: status,
	}
	const tags = { feature: 'lifecycle-persistence', action: options.action }

	if (status != null && status >= 400 && status < 500) {
		logger.warn(`lifecycle-persistence ${options.action} failed with ${status}`, {
			category: 'state',
			tags,
			data,
		})
		return
	}

	logger.error(error, { category: 'state', tags, data })
}

export type LifecycleState = 'idle' | 'pending' | 'generating' | 'tool-executing' | 'ready'
export type PersistenceState = 'idle' | 'debouncing' | 'saving' | 'error'

interface UseLifecyclePersistenceOptions<T> {
	// Pass in data from tanstack query here. You may transform it first to a FE-satisfying format.
	data: T | undefined

	// Determine the states from the data.
	isIdle: (d: T | undefined) => boolean
	isPending: (d: T | undefined) => boolean
	isGenerating: (d: T | undefined) => boolean

	// Whether a chat tool is currently executing on this resource
	isToolExecuting?: boolean

	// Save the data to the server, typically a tanstack mutation. Be sure to clear the query
	// cache after the save, if relevant.
	save: (d: T) => Promise<void>

	// How long to debounce the save.
	debounceMs?: number

	// Whether the data is locked (e.g. while something is happening on the UI)
	isLocked?: boolean

	// Whether to log debug messages.
	debug?: boolean

	// Optional name used to prefix debug logs for easier tracing.
	name?: string

	// If the data has a version, you can use this to lock the sync until the version changes.
	// Typically, this is the updated_at field. This prevents stale data from appearing
	// after a save, but allows new data to sync if the updated timestamp is newer.
	getVersion?: (d: T | undefined) => number | string | Date | null
}

export type UseLifecyclePersistenceReturns<T> = {
	data: T | undefined
	setData: (next: T) => void
	flush: () => Promise<void>
	lifecycle: LifecycleState
	persistence: PersistenceState
	isLocked: boolean
}

export function useLifecyclePersistence<T>(
	opts: UseLifecyclePersistenceOptions<T>,
): UseLifecyclePersistenceReturns<T> {
	const {
		data,
		isIdle,
		isPending,
		isGenerating,
		isToolExecuting = false,
		save,
		debounceMs = 300,
		isLocked = false,
		debug = false,
		name,
	} = opts

	const debugPrefix = `[useLifecyclePersistence${name ? `:${name}` : ''}]`

	// Local state
	const [localData, setLocalData] = useState<T | undefined>(data)
	const [lifecycle, setLifecycle] = useState<LifecycleState>('idle')
	const [persistence, setPersistence] = useState<PersistenceState>('idle')
	const lockUntilVersionRef = useRef<number | string | Date | null>(null)
	const timeoutRef = useRef<NodeJS.Timeout | null>(null)
	const localDataRef = useRef<T | undefined>(localData)
	const hasPendingChangeRef = useRef(false)

	// Save fn ref (avoids effect loops if save identity changes)
	const saveRef = useRef(save)
	useEffect(() => {
		saveRef.current = save
	}, [save])

	// Derive lifecycle from server data and tool execution state
	useEffect(() => {
		let next: LifecycleState
		// Tool execution takes precedence when content is ready
		if (isToolExecuting && !isIdle(data) && !isPending(data) && !isGenerating(data)) {
			next = 'tool-executing'
		} else if (isIdle(data)) {
			next = 'idle'
		} else if (isPending(data)) {
			next = 'pending'
		} else if (isGenerating(data)) {
			next = 'generating'
		} else {
			next = 'ready'
		}

		setLifecycle((prev) => {
			if (prev !== next) {
				if (debug) { console.log(debugPrefix, '[lifecycle]', prev, '→', next) }
				return next
			}
			return prev
		})
	}, [data, isIdle, isPending, isGenerating, isToolExecuting, debug])

	// Sync localData from server except while debouncing or saving
	useEffect(() => {
		if (persistence === 'debouncing' || persistence === 'saving' || persistence === 'error') {
			if (debug) { console.debug(debugPrefix, '[persistence] skip sync: debouncing/saving/error') }
			return
		}
		if (data == null) { return }

		if (debug) {
			console.debug(
				debugPrefix,
				'[sync] proceeding with sync',
				`persistence: ${persistence}, lifecycle: ${lifecycle}, lockUntilVersion: ${lockUntilVersionRef.current}`,
			)
		}

		// If waiting for server version change, avoid syncing until it's newer
		if (lockUntilVersionRef.current != null && opts.getVersion) {
			let current: number | string | Date | null = null
			try {
				current = opts.getVersion(data)
			} catch {
				current = null
			}
			// Skip sync if server version is older or equal (prevents race condition where
			// stale server responses overwrite newer local changes)
			if (current != null && current <= lockUntilVersionRef.current) {
				if (debug) {
					console.debug(
						debugPrefix,
						'[sync] sync: server version is not newer, skipping',
						current,
						'<=',
						lockUntilVersionRef.current,
					)
				}
				return
			}
			if (debug) {
				console.debug(
					debugPrefix,
					'[sync] sync: server version is newer, unlocking',
					current,
					lockUntilVersionRef.current,
				)
			}
			lockUntilVersionRef.current = null
		} else {
			if (debug) { console.debug(debugPrefix, '[sync] sync: not waiting for server version change') }
		}

		if (debug) { console.debug(debugPrefix, '[sync] sync', data, localData) }

		setLocalData((prev) => {
			// If already same ref, skip
			if (prev === data) { return prev }

			// Optional: shallow equality via JSON.stringify
			if (
				typeof prev === 'object'
				&& typeof data === 'object'
				&& prev != null
				&& JSON.stringify(prev) === JSON.stringify(data)
			) {
				if (debug) { console.debug(debugPrefix, '[sync] localData <= server data: shallow equality, skipping') }
				return prev
			}

			if (debug) { console.debug(debugPrefix, '[sync] localData <= server data') }
			return data
		})
	}, [data, persistence, opts.getVersion, debug])

	// Debounce + save effect
	useEffect(() => {
		if (localData == null) {
			if (debug) { console.debug(debugPrefix, '[save] skip: localData is null', localData) }
			return
		}
		if (persistence !== 'debouncing') {
			if (debug) { console.debug(debugPrefix, '[save] skip: not debouncing', localData, persistence) }
			return
		}

		const handler = setTimeout(async () => {
			// Use the ref to get the latest localData at execution time, not closure time
			const dataToSave = localDataRef.current
			if (dataToSave == null) {
				if (debug) { console.debug(debugPrefix, '[save] skip: localData became null') }
				setPersistence('idle')
				return
			}

			setPersistence('saving')
			try {
				// Capture version from the data being saved (latest local changes)
				if (opts.getVersion) {
					try {
						lockUntilVersionRef.current = opts.getVersion(dataToSave)
					} catch {
						lockUntilVersionRef.current = null
					}
					if (debug) { console.debug(debugPrefix, '[save] captured version', lockUntilVersionRef.current) }
				}
				if (debug) { console.debug(debugPrefix, '[save] start', dataToSave) }
				await saveRef.current(dataToSave)
			} catch (error) {
				if (debug) { console.error(debugPrefix, '[save] error', error) }
				reportSaveFailure(error, { resourceName: name, action: 'save', lifecycleState: lifecycle })
				hasPendingChangeRef.current = false
				setPersistence('error')
				return
			} finally {
				if (debug) { console.debug(debugPrefix, '[save] end', dataToSave) }
			}
			// If there was a change during the save, transition to debouncing. Otherwise, idle.
			if (hasPendingChangeRef.current) {
				hasPendingChangeRef.current = false
				setPersistence('debouncing')
			} else {
				setPersistence('idle')
			}
		}, debounceMs)

		timeoutRef.current = handler
		return () => {
			clearTimeout(handler)
			timeoutRef.current = null
		}
	}, [localData, persistence, debounceMs, data, opts.getVersion, debug])

	// Exposed setter
	const setData = useCallback(
		(next: T) => {
			if (isLocked || lifecycle === 'pending' || lifecycle === 'generating' || lifecycle === 'tool-executing') {
				if (debug) {
					console.warn(
						debugPrefix,
						'[setData] Blocked while locked, pending, generating, or tool-executing',
					)
				}
				return // soft fail: no state change
			}
			if (debug) { console.debug(debugPrefix, '[setData] setLocalData', next) }
			setLocalData(next)
			setPersistence(prev => {
				// If already saving, mark that there's a pending change and keep state as 'saving'
				if (prev === 'saving') {
					hasPendingChangeRef.current = true
					return 'saving'
				}
				// Otherwise transition to debouncing (also clears error state, retrying the save)
				return 'debouncing'
			})
		},
		[isLocked, lifecycle, debug],
	)

	// Keep ref in sync with latest localData
	useEffect(() => {
		localDataRef.current = localData
		if (debug) { console.debug(debugPrefix, '[localData]', localData) }
	}, [localData, debug])

	// Flush function - immediately save, skip debounce
	const flush = useCallback(async () => {
		// Cancel any pending debounce
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
			timeoutRef.current = null
		}

		// Use the ref to get the latest localData
		const dataToSave = localDataRef.current

		// If no data, nothing to save
		if (dataToSave == null) {
			if (debug) { console.debug(debugPrefix, '[flush] no data to save') }
			return
		}

		// If already saving, skip
		if (persistence === 'saving') {
			if (debug) { console.debug(debugPrefix, '[flush] already saving, skipping') }
			return
		}

		// Save immediately
		setPersistence('saving')
		try {
			if (opts.getVersion) {
				try {
					lockUntilVersionRef.current = opts.getVersion(dataToSave)
				} catch {
					lockUntilVersionRef.current = null
				}
				if (debug) { console.debug(debugPrefix, '[flush] captured version', lockUntilVersionRef.current) }
			}
			if (debug) { console.debug(debugPrefix, '[flush] immediate save', dataToSave) }
			await saveRef.current(dataToSave)
		} catch (error) {
			if (debug) { console.error(debugPrefix, '[flush] error', error) }
			reportSaveFailure(error, { resourceName: name, action: 'flush', lifecycleState: lifecycle })
			hasPendingChangeRef.current = false
			setPersistence('error')
			return
		} finally {
			if (debug) { console.debug(debugPrefix, '[flush] complete') }
		}
		// If there was a change during the save, transition to debouncing. Otherwise, idle.
		if (hasPendingChangeRef.current) {
			hasPendingChangeRef.current = false
			setPersistence('debouncing')
		} else {
			setPersistence('idle')
		}
	}, [persistence, data, opts.getVersion, debug, debugPrefix])

	return {
		data: localData,
		setData,
		flush,
		lifecycle,
		persistence,
		isLocked: isLocked || lifecycle === 'pending' || lifecycle === 'generating' || lifecycle === 'tool-executing',
	}
}
